/**
 * Maintenance Equipment Linker
 *
 * Utility for linking equipment items to maintenance jobs and managing service history.
 * Supports three tracking patterns:
 * 1. Individual tracking (fire extinguishers, liferafts) - per-unit servicing
 * 2. Group tracking (detectors) - bulk testing
 * 3. Simple tracking (visual checks) - no equipment linking
 */

/**
 * Map maintenance job types to equipment register types
 * @param {string} jobType - Job type from maintenance (e.g., "Fire Extinguisher - Basic Service")
 * @returns {Object|null} { type, namePattern } or null if no mapping
 */
function mapJobTypeToEquipmentType(jobType) {
  const normalizedJob = jobType.toLowerCase();

  // Map maintenance job names to equipment register type codes
  if (normalizedJob.includes('fire extinguisher') || normalizedJob.includes('extinguisher')) return { type: 'extinguisher' };
  if (normalizedJob.includes('fire hose') || normalizedJob.includes('hose')) return { type: 'hose' };
  if (normalizedJob.includes('fire blanket')) return { type: 'blanket' };
  if (normalizedJob.includes('fire pump') || normalizedJob.includes('pump')) return { type: 'pump' };
  if (normalizedJob.includes('lifebuoy')) return { type: 'lifebuoy' };
  if (normalizedJob.includes('lifejacket') || normalizedJob.includes('life jacket')) return { type: 'lifejacket' };
  if (normalizedJob.includes('liferaft') || normalizedJob.includes('life raft')) return { type: 'liferaft' };
  if (normalizedJob.includes('epirb')) return { type: 'epirb' };
  if (normalizedJob.includes('sart')) return { type: 'sart' };

  // DETECTORS: Use both type AND name pattern to differentiate
  // Fire Detection System → type: "detector" + name includes "fire"
  if (normalizedJob.includes('fire detection') || normalizedJob.includes('smoke detector') || normalizedJob.includes('heat detector')) {
    return { type: 'detector', namePattern: 'fire' };
  }
  // CO Detection System → type: "gas" + name includes "co"
  if (normalizedJob.includes('co detection') || normalizedJob.includes('carbon monoxide')) {
    return { type: 'gas', namePattern: 'co' };
  }
  // Gas Detection System → type: "gas" + name includes "hydrocarbon" or "gas" (but NOT "co")
  if (normalizedJob.includes('gas detection') || normalizedJob.includes('hydrocarbon')) {
    return { type: 'gas', namePattern: 'hydrocarbon' };
  }

  if (normalizedJob.includes('bilge pump')) return { type: 'pump' };
  if (normalizedJob.includes('emergency lighting')) return { type: 'lighting' };
  if (normalizedJob.includes('navigation lights')) return { type: 'lights' };

  return null;
}

/**
 * Get equipment linked to a maintenance job
 * @param {Object} params
 * @param {string} params.jobType - Equipment type (e.g., 'Fire Extinguisher - Basic Service')
 * @param {string} params.category - Equipment category (FFE, LSA, Navigation, etc.)
 * @param {Array} params.equipmentList - All equipment records
 * @param {Object} params.unitsData - Units storage object keyed by family ID (from units_simple storage)
 * @param {string} params.location - Optional location filter
 * @returns {Object} { familyCount, unitCount, families }
 */
export function getLinkedEquipment({ jobType, category, equipmentList = [], unitsData = {}, location = null }) {

  // Map the maintenance job type to equipment register type
  const equipmentTypeMapping = mapJobTypeToEquipmentType(jobType);

  if (!equipmentTypeMapping) {

    return { familyCount: 0, unitCount: 0, families: [] };
  }

  // Filter equipment families that match the mapped type and category
  const matchingFamilies = equipmentList.filter(family => {
    // Equipment register uses 'type' field (short codes like 'extinguisher', 'pump', etc.)
    const familyType = family.type || family.equipmentType;
    const familyName = (family.name || '').toLowerCase();

    // Type must match
    const typeMatch = familyType === equipmentTypeMapping.type;

    // If namePattern specified, equipment name must contain it
    const nameMatch = !equipmentTypeMapping.namePattern ||
                      familyName.includes(equipmentTypeMapping.namePattern);

    const categoryMatch = !category || (family.category && family.category.toUpperCase() === category.toUpperCase());
    const locationMatch = !location || family.location === location;

    return typeMatch && nameMatch && categoryMatch && locationMatch;
  });

  // Enrich families with their units from separate storage
  const enrichedFamilies = matchingFamilies.map(family => {
    const units = unitsData[family.id] || [];

    return {
      ...family,
      units
    };
  });

  // Count total units across all families
  const totalUnits = enrichedFamilies.reduce((sum, family) => {
    return sum + (family.units?.length || 0);
  }, 0);

  return {
    familyCount: enrichedFamilies.length,
    unitCount: totalUnits,
    families: enrichedFamilies
  };
}

/**
 * Get units that need service based on maintenance job schedule
 * @param {Object} params
 * @param {Array} params.families - Equipment families
 * @param {number} params.intervalMonths - Service interval in months
 * @param {number} params.warningDays - Days before due to warn (default 30)
 * @returns {Object} { overdue, dueSoon, scheduled, units }
 */
export function getUnitsNeedingService({ families, intervalMonths, warningDays = 30 }) {
  const today = new Date();
  const units = [];

  families.forEach(family => {
    (family.units || []).forEach(unit => {
      const lastService = getLastServiceDate(unit);
      const status = getServiceStatus(lastService, intervalMonths, warningDays);

      units.push({
        ...unit,
        familyId: family.id,
        familyType: family.type,
        location: family.location,
        lastService,
        status
      });
    });
  });

  // Sort by status priority: overdue > dueSoon > scheduled
  units.sort((a, b) => {
    const priority = { overdue: 0, dueSoon: 1, scheduled: 2 };
    return priority[a.status] - priority[b.status];
  });

  const overdue = units.filter(u => u.status === 'overdue').length;
  const dueSoon = units.filter(u => u.status === 'dueSoon').length;
  const scheduled = units.filter(u => u.status === 'scheduled').length;

  return {
    overdue,
    dueSoon,
    scheduled,
    units
  };
}

/**
 * Get the most recent service date for a unit
 * @param {Object} unit - Equipment unit
 * @returns {string|null} ISO date string or null
 */
function getLastServiceDate(unit) {
  // Check serviceHistory array first (new system)
  if (unit.serviceHistory && Array.isArray(unit.serviceHistory) && unit.serviceHistory.length > 0) {
    const sortedHistory = [...unit.serviceHistory].sort((a, b) =>
      new Date(b.date) - new Date(a.date)
    );
    return sortedHistory[0].date;
  }

  // Fallback to lastServiceDate (legacy field)
  return unit.lastServiceDate || null;
}

/**
 * Calculate service status based on last service date and interval
 * @param {string|null} lastServiceDate - ISO date string
 * @param {number} intervalMonths - Service interval in months
 * @param {number} warningDays - Days before due to warn
 * @returns {string} 'overdue' | 'dueSoon' | 'scheduled' | 'never'
 */
function getServiceStatus(lastServiceDate, intervalMonths, warningDays = 30) {
  if (!lastServiceDate) return 'never';

  const today = new Date();
  const lastService = new Date(lastServiceDate);
  const nextDue = new Date(lastService);
  nextDue.setMonth(nextDue.getMonth() + intervalMonths);

  const daysUntilDue = Math.floor((nextDue - today) / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= warningDays) return 'dueSoon';
  return 'scheduled';
}

/**
 * Update equipment after maintenance completion (FLAT STRUCTURE)
 * @param {Object} params
 * @param {Array} params.selectedUnits - Array of equipment IDs that were serviced
 * @param {Array} params.equipmentList - All equipment records (FLAT ARRAY, not families)
 * @param {Object} params.serviceRecord - Service record data
 * @param {string} params.serviceRecord.date - Service date
 * @param {string} params.serviceRecord.type - Service type display name (e.g., 'Fire Extinguisher - Basic Service')
 * @param {string} params.serviceRecord.jobId - Maintenance job ID
 * @param {string} params.serviceRecord.serviceType - Normalized service type (e.g., 'extinguisher_basic')
 * @param {string} params.serviceRecord.inspector - Person who performed service
 * @param {number} params.intervalMonths - Service interval for calculating next due
 * @returns {Array} Updated equipment list
 */
export function updateEquipmentAfterMaintenance({
  selectedUnits,
  equipmentList,
  serviceRecord,
  intervalMonths
}) {

  // FLAT STRUCTURE: Equipment list is already a flat array
  const updatedEquipment = equipmentList.map(item => {
    // Check if this item was serviced
    if (!selectedUnits.includes(item.id)) {
      return item;
    }

    // Calculate next service date
    const nextDue = new Date(serviceRecord.date);
    nextDue.setMonth(nextDue.getMonth() + intervalMonths);
    const nextCheckDue = nextDue.toISOString().split('T')[0];

    // Create new service history entry with normalized serviceType
    const historyEntry = {
      serviceType: serviceRecord.serviceType, // CRITICAL: Use normalized service type
      type: serviceRecord.type, // Display name for UI compatibility
      date: serviceRecord.date,
      nextDue: nextCheckDue,
      jobId: serviceRecord.jobId,
      inspector: serviceRecord.inspector
    };

    // Initialize serviceHistory if it doesn't exist
    const serviceHistory = item.serviceHistory || [];

    const updatedItem = {
      ...item,
      lastServiceAt: serviceRecord.date, // FIXED: Use correct field name
      nextCheckDue: nextCheckDue, // CRITICAL: Update the nextCheckDue field for Equipment Register UI
      serviceHistory: [...serviceHistory, historyEntry]
    };

    return updatedItem;
  });

  return updatedEquipment;
}

/**
 * Get service history for a specific unit
 * @param {Object} unit - Equipment unit
 * @returns {Array} Service history records, sorted newest first
 */
export function getUnitServiceHistory(unit) {
  if (!unit.serviceHistory || !Array.isArray(unit.serviceHistory)) {
    return [];
  }

  return [...unit.serviceHistory].sort((a, b) =>
    new Date(b.date) - new Date(a.date)
  );
}

/**
 * Format service status for display
 * @param {string} status - Service status
 * @returns {Object} { text, color }
 */
export function formatServiceStatus(status) {
  const formats = {
    overdue: { text: 'Overdue', color: 'red' },
    dueSoon: { text: 'Due Soon', color: 'yellow' },
    scheduled: { text: 'Scheduled', color: 'green' },
    never: { text: 'Never Serviced', color: 'gray' }
  };

  return formats[status] || formats.scheduled;
}

/**
 * Check if a maintenance job should show equipment linking
 * @param {string} equipmentType - Equipment type from maintenance job (e.g., "Fire Extinguisher - Basic Service")
 * @returns {boolean}
 */
export function shouldLinkEquipment(equipmentType) {
  if (!equipmentType) return false;

  // Use the mapping function to check if there's a mapping defined
  const mapping = mapJobTypeToEquipmentType(equipmentType);
  return mapping !== null;
}
